<template>
    <div class="cms-container">
        <div class="cms-sidebar">
            <div class="sidebar-title">组件库</div>
            <draggable class="widget-list" :list="widgetList" :group="{ name: 'widgets', pull: 'clone', put: false }"
                :clone="cloneWidget" :sort="false">
                <div class="widget-item" v-for="item in widgetList" :key="item.type">
                    <a-icon :type="item.icon" class="widget-icon" />
                    <span>{{ item.label }}</span>
                </div>
            </draggable>
            <div class="instruction">
                <p>1. 拖拽组件到右侧区域</p>
                <p>2. 放到已有组件旁可自动并列</p>
                <p>3. 点击组件选中后进行编辑</p>
            </div>
        </div>

        <div class="cms-work-area" @click.self="clearSelection">

            <div class="area-header">
                <span style="margin-right: 15px;">页面布局预览</span>
                <span style="font-size: 12px; color: #666;">缩放比例: {{ (scaleRate * 100).toFixed(0) }}%</span>
                <a-slider v-model="scaleRate" :min="0.5" :max="1.5" :step="0.05"
                    style="width: 150px; display: inline-block; vertical-align: middle; margin-left: 10px;" />
            </div>

            <div class="canvas-outer-wrapper" @click.self="clearSelection">
                <div class="canvas-wrapper" :style="canvasStyle">
                    <draggable v-model="rows" :group="{ name: 'row_root', put: ['widgets', 'row_root'] }"
                        animation="300" handle=".row-drag-handle" class="canvas-list" @change="onRowChange">
                        <div v-for="(row, rowIndex) in rows" :key="row.tempId || row.id" class="cms-row">

                            <div class="row-handle-group">
                                <a-icon type="drag" class="row-drag-handle" title="拖拽行排序" />
                                <a-icon type="delete" class="row-delete-btn" @click="deleteRow(rowIndex)"
                                    title="删除整行" />
                            </div>

                            <draggable v-if="row.children" v-model="row.children" group="widgets" animation="300"
                                class="row-flex-container" @change="onWidgetChange(row)">
                                <div v-for="(element, colIndex) in row.children" :key="colIndex"
                                    class="cms-component-wrapper"
                                    :class="{ 'is-selected': selectedId === element.tempId }"
                                    @click.stop="handleSelect(element, row)">

                                    <div class="preview-media-box">
                                        <div class="gray-placeholder">
                                            <a-icon :type="getIconByType(element.type)"
                                                style="font-size: 24px; margin-bottom: 8px;" />
                                            <div class="resource-name">{{ element.contentData.filename || '未绑定资源' }}
                                            </div>
                                            <div class="type-tag">{{ getLabelByType(element.type) }}</div>
                                        </div>

                                        <div v-if="element.contentData.title" class="media-caption"
                                            :class="element.contentData.titleMode === 'overlay' ? 'mode-overlay' : 'mode-outside'"
                                            :style="getUniversalTextStyle(element.contentData)">
                                            {{ element.contentData.title }}
                                        </div>
                                    </div>

                                    <div class="selected-mask" v-if="selectedId === element.tempId">
                                        <div class="action-bar">
                                            <a-tag color="blue">{{ getLabelByType(element.type) }}</a-tag>
                                            <a-button type="danger" size="small" icon="delete"
                                                @click.stop="deleteWidget(row, colIndex)">删除</a-button>
                                        </div>
                                    </div>

                                </div>
                            </draggable>

                            <div v-if="row.children && row.children.length === 0" class="empty-row-tip">
                                (空行，保存后将自动移除)
                            </div>

                        </div>
                    </draggable>

                    <a-empty v-if="rows.length === 0"
                        image="https://gw.alipayobjects.com/zos/antfincdn/ZHrcdLPrvN/empty.svg"
                        :image-style="{ height: '60px' }" description="从左侧拖拽组件"
                        style="padding-top: 100px; pointer-events: none;" />
                </div>
            </div>
        </div>

        <div class="cms-properties">
            
            <template v-if="selectedItem">
                <div class="prop-header">
                    编辑{{ getLabelByType(selectedItem.type) }}
                    <a-button type="link" size="small" style="float:right" @click="clearSelection">关闭</a-button>
                </div>

                <a-form-model layout="vertical">
                    <a-form-model-item label="标题/文案内容">
                        <a-input v-model="selectedItem.contentData.title" />
                    </a-form-model-item>

                    <a-divider>文字样式设置</a-divider>

                    <a-row :gutter="10">
                        <a-col :span="12">
                            <a-form-model-item label="文字大小 (px)">
                                <a-input-number v-model="selectedItem.contentData.fontSize" :min="12" :max="100" style="width: 100%;" />
                            </a-form-model-item>
                        </a-col>
                        <a-col :span="12">
                            <a-form-model-item label="文字颜色">
                                <div style="display:flex; align-items: center;">
                                    <input type="color" v-model="selectedItem.contentData.color"
                                        style="width:32px; height:32px; border:1px solid #ddd; padding:0; margin-right:8px; cursor:pointer; background: none;" />
                                    <a-input v-model="selectedItem.contentData.color" placeholder="#333" size="small" />
                                </div>
                            </a-form-model-item>
                        </a-col>
                    </a-row>

                    <a-form-model-item label="对齐方式">
                        <a-radio-group v-model="selectedItem.contentData.textAlign" button-style="solid" size="small">
                            <a-radio-button value="left">左对齐</a-radio-button>
                            <a-radio-button value="center">居中</a-radio-button>
                            <a-radio-button value="right">右对齐</a-radio-button>
                        </a-radio-group>
                    </a-form-model-item>

                    <a-divider>资源设置</a-divider>

                    <a-form-model-item label="资源文件名">
                        <a-input v-model="selectedItem.contentData.filename" placeholder="例如: banner01.jpg" />
                    </a-form-model-item>

                    <a-form-model-item label="标题显示模式">
                        <a-radio-group v-model="selectedItem.contentData.titleMode" button-style="solid">
                            <a-radio-button value="outside">下方显示</a-radio-button>
                            <a-radio-button value="overlay">覆盖图上</a-radio-button>
                        </a-radio-group>
                    </a-form-model-item>

                    <a-form-model-item label="覆盖位置" v-if="selectedItem.contentData.titleMode === 'overlay'">
                        <div class="grid-selector">
                            <div v-for="i in 9" :key="i" class="grid-cell"
                                :class="{ active: selectedItem.contentData.titleAlign === i }"
                                @click="selectedItem.contentData.titleAlign = i"></div>
                        </div>
                    </a-form-model-item>

                    <a-divider />
                    <a-button type="primary" block icon="save" @click="saveCurrentRow">保存修改</a-button>
                </a-form-model>
            </template>

            <div v-else class="empty-properties">
                <a-empty description="请点击中间画布的组件进行编辑" :image="simpleImage" />
            </div>

        </div>
    </div>
</template>

<script>
import draggable from 'vuedraggable'
import { Empty } from 'ant-design-vue';
import { getCmsComponents, saveCmsComponent, deleteCmsComponent, sortCmsComponents } from '@/api'

export default {
    components: { draggable },
    data() {
        return {
            simpleImage: Empty.PRESENTED_IMAGE_SIMPLE,
            scaleRate: 1.0,
            widgetList: [
                { type: 'video', label: '视频', icon: 'video-camera' },
                { type: 'image', label: '图片', icon: 'picture' },
                { type: 'carousel', label: '轮播图', icon: 'appstore' }
            ],
            rows: [],
            selectedId: null,
            selectedItem: null,
            selectedRow: null,
        }
    },
    computed: {
        canvasStyle() {
            return {
                transform: `scale(${this.scaleRate})`,
                transformOrigin: 'top center',
                width: '375px',
                minHeight: '667px',
                height: 'auto'
            };
        }
    },
    created() {
        this.loadData();
    },
    methods: {
        loadData() {
            getCmsComponents().then(res => {
                this.rows = res.data.map(item => {
                    let children = [];
                    try {
                        const parsed = JSON.parse(item.content);
                        if (parsed && parsed.children) children = parsed.children;
                    } catch (e) { console.error(e) }

                    return {
                        ...item,
                        children: children.map(child => ({
                            ...child,
                            tempId: Math.random().toString(36).substr(2)
                        }))
                    };
                });
            });
        },

        cloneWidget(origin) {
            return {
                type: origin.type,
                tempId: Math.random().toString(36).substr(2),
                contentData: {
                    title: '默认描述文字',
                    filename: '',
                    titleMode: 'outside',
                    titleAlign: 5,
                    fontSize: 14, // 修改：默认存数字
                    color: '#333333',
                    textAlign: 'center'
                }
            }
        },

        onRowChange(evt) {
            if (evt.added) {
                const widget = evt.added.element;
                const newRow = {
                    id: null,
                    type: 'layout_row',
                    sortOrder: evt.added.newIndex,
                    children: [widget]
                };
                this.$set(this.rows, evt.added.newIndex, newRow);
                this.saveRowToBackend(newRow);
            }
            if (evt.moved) {
                this.updateSort();
            }
        },

        onWidgetChange(row) {
            this.saveRowToBackend(row);
            if (row.children.length === 0 && row.id) {
                deleteCmsComponent({ id: row.id }).then(() => {
                    this.rows = this.rows.filter(r => r !== row);
                });
            }
        },

        handleSelect(item, row) {
            this.selectedId = item.tempId;
            // 兼容旧数据：如果 fontSize 是字符串 '14px'，尝试转为数字 14
            if (typeof item.contentData.fontSize === 'string') {
                item.contentData.fontSize = parseInt(item.contentData.fontSize) || 14;
            }
            this.selectedItem = item;
            this.selectedRow = row;
        },

        clearSelection() {
            this.selectedId = null;
            this.selectedItem = null;
        },

        saveCurrentRow() {
            if (this.selectedRow) {
                this.saveRowToBackend(this.selectedRow);
            }
        },

        saveRowToBackend(row) {
            const dataToSave = {
                id: row.id,
                name: 'Layout Row',
                type: 'layout_row',
                sortOrder: row.sortOrder || 0,
                content: JSON.stringify({ children: row.children })
            };

            saveCmsComponent(dataToSave).then(res => {
                if (!row.id && res.data && res.data.id) {
                    row.id = res.data.id;
                }
                this.$message.success('已保存');
            });
        },

        updateSort() {
            const ids = this.rows.map(r => r.id).filter(id => id);
            sortCmsComponents(ids);
        },

        deleteWidget(row, index) {
            row.children.splice(index, 1);
            this.clearSelection();
            this.saveRowToBackend(row);
            if (row.children.length === 0 && row.id) {
                deleteCmsComponent({ id: row.id }).then(() => {
                    this.rows = this.rows.filter(r => r !== row);
                });
            }
        },

        deleteRow(index) {
            const row = this.rows[index];
            if (row.id) {
                deleteCmsComponent({ id: row.id }).then(() => {
                    this.rows.splice(index, 1);
                    this.$message.success('行已删除');
                });
            } else {
                this.rows.splice(index, 1);
            }
        },

        getIconByType(type) {
            const map = { video: 'video-camera', image: 'picture', carousel: 'appstore' };
            return map[type] || 'file';
        },
        getLabelByType(type) {
            const map = { video: '视频', image: '图片', carousel: '轮播' };
            return map[type] || '组件';
        },

        getUniversalTextStyle(data) {
            const baseStyle = {
                fontSize: (data.fontSize || 14) + 'px', // 修改：自动补齐单位
                color: data.color || '#333',
                textAlign: data.textAlign || 'left',
                lineHeight: '1.5'
            };

            if (data.titleMode !== 'overlay') {
                return baseStyle;
            }

            const align = data.titleAlign || 5;
            const overlayStyle = {
                ...baseStyle,
                position: 'absolute',
                color: data.color || '#fff',
                fontWeight: 'bold',
                // 修改：移除了 textShadow (虚化效果)
                pointerEvents: 'none',
                width: '100%',
                padding: '0 10px'
            };

            if ([1, 2, 3].includes(align)) overlayStyle.top = '10px';
            if ([4, 5, 6].includes(align)) { overlayStyle.top = '50%'; overlayStyle.transform = 'translateY(-50%)'; }
            if ([7, 8, 9].includes(align)) overlayStyle.bottom = '10px';
            
            if ([1, 4, 7].includes(align)) overlayStyle.textAlign = 'left';
            if ([2, 5, 8].includes(align)) overlayStyle.textAlign = 'center';
            if ([3, 6, 9].includes(align)) overlayStyle.textAlign = 'right';

            return overlayStyle;
        }
    }
}
</script>

<style scoped>
.cms-container {
    display: flex;
    height: calc(100vh - 100px);
    background: #f5f7fa;
}

.cms-sidebar {
    width: 220px;
    background: #fff;
    padding: 15px;
    border-right: 1px solid #e8e8e8;
    display: flex;
    flex-direction: column;
    flex-shrink: 0; 
}

.sidebar-title {
    font-weight: bold;
    margin-bottom: 15px;
    font-size: 16px;
}

.widget-item {
    padding: 12px;
    margin-bottom: 10px;
    background: #fff;
    border: 1px solid #d9d9d9;
    border-radius: 4px;
    cursor: grab;
    display: flex;
    align-items: center;
    transition: all 0.2s;
}

.widget-item:hover {
    border-color: #1890ff;
    color: #1890ff;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.widget-icon {
    margin-right: 10px;
    font-size: 18px;
}

.instruction {
    margin-top: auto;
    font-size: 12px;
    color: #888;
    background: #f9f9f9;
    padding: 10px;
    border-radius: 4px;
}

.cms-work-area {
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    min-width: 0; 
}

.area-header {
    height: 50px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #fff;
    border-bottom: 1px solid #eee;
    padding: 0 20px;
    flex-shrink: 0;
}

.canvas-outer-wrapper {
    flex: 1;
    overflow-y: auto;
    background: #f0f2f5;
    padding: 40px 0;
    display: flex;
    justify-content: center;
    align-items: flex-start;
}

.canvas-wrapper {
    background: #fff;
    box-shadow: 0 0 20px rgba(0, 0, 0, 0.1);
    transition: transform 0.2s ease-out;
}

.canvas-list {
    min-height: 600px;
    padding-bottom: 50px;
}

.cms-row {
    position: relative;
    margin-bottom: 0;
    border: 1px dashed transparent;
}

.cms-row:hover {
    border-color: #1890ff;
    z-index: 5;
}

.cms-row:hover .row-handle-group {
    display: flex;
}

.row-handle-group {
    display: none;
    position: absolute;
    left: -32px;
    top: 0;
    flex-direction: column;
}

.row-drag-handle,
.row-delete-btn {
    width: 28px;
    height: 28px;
    line-height: 28px;
    text-align: center;
    background: #fff;
    border: 1px solid #d9d9d9;
    cursor: pointer;
    margin-bottom: 5px;
    border-radius: 2px;
    color: #666;
    font-size: 14px;
}

.row-delete-btn:hover {
    color: red;
    border-color: red;
}

.row-flex-container {
    display: flex;
    min-height: 50px;
}

.cms-component-wrapper {
    flex: 1;
    position: relative;
    cursor: pointer;
    border: 2px solid transparent;
    transition: border-color 0.2s;
}

.cms-component-wrapper:hover {
    border-color: #a6d4ff;
    z-index: 2;
}

.cms-component-wrapper.is-selected {
    border-color: #1890ff;
    z-index: 10;
}

.preview-media-box {
    position: relative;
    background: #fff;
    overflow: hidden;
}

.gray-placeholder {
    background: #e6e6e6;
    height: 180px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    color: #999;
}

.resource-name {
    font-size: 12px;
    color: #666;
    background: rgba(255, 255, 255, 0.5);
    padding: 2px 6px;
    border-radius: 2px;
}

.type-tag {
    position: absolute;
    top: 5px;
    left: 5px;
    font-size: 10px;
    color: #fff;
    background: rgba(0, 0, 0, 0.3);
    padding: 1px 4px;
    border-radius: 2px;
}

.media-caption.mode-outside {
    padding: 10px;
    background: #fff;
}

.media-caption.mode-overlay {
    pointer-events: none;
    z-index: 2;
}

.selected-mask {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    pointer-events: none;
    border: 1px solid #1890ff; 
}

.action-bar {
    position: absolute;
    top: -30px;
    right: 0;
    pointer-events: auto;
    display: flex;
    gap: 5px;
    background: #fff;
    padding: 2px;
    border-radius: 2px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.cms-properties {
    width: 320px;
    background: #fff;
    padding: 20px;
    border-left: 1px solid #e8e8e8;
    overflow-y: auto;
    flex-shrink: 0; 
}

.empty-properties {
    margin-top: 100px;
    text-align: center;
    color: #999;
}

.prop-header {
    font-size: 16px;
    font-weight: bold;
    margin-bottom: 20px;
    padding-bottom: 10px;
    border-bottom: 1px solid #eee;
}

.grid-selector {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 5px;
    width: 120px;
    margin: 0 auto;
}

.grid-cell {
    width: 36px;
    height: 36px;
    background: #f0f0f0;
    border: 1px solid #ddd;
    cursor: pointer;
    border-radius: 2px;
}

.grid-cell.active {
    background: #1890ff;
    border-color: #1890ff;
}
</style>